"use client"

import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from "react"
import {
  loginWithBackend,
  logout as authLogout,
  selectTenant as authSelectTenant,
  getUser,
  setUser as authSetUser,
  getTenant,
  getToken,
  hasPermission,
  canView,
  canCreate,
  canEdit,
  canDelete,
  getRolePermissions,
  isRoleHigherOrEqual,
  loadPermissionsFromAPI,
  type AuthUser,
  type AuthTenant,
  type UserRole,
  type PermissionModule,
  type PermissionAction,
} from "./auth"
import { PermissionsProvider } from "./permissions-context"

interface AuthContextType {
  user: AuthUser | null
  tenant: AuthTenant | null
  isLoading: boolean
  isAuthenticated: boolean
  token: string | null

  // Auth actions
  login: (email: string, password: string) => Promise<{ success: boolean; user?: AuthUser; tenants?: AuthTenant[]; error?: string }>
  logout: () => void
  selectTenant: (tenant: AuthTenant) => void
  updateTenant: (tenant: AuthTenant) => void

  // Permission helpers
  hasPermission: (module: PermissionModule, action: PermissionAction) => boolean
  canView: (module: PermissionModule) => boolean
  canCreate: (module: PermissionModule) => boolean
  canEdit: (module: PermissionModule) => boolean
  canDelete: (module: PermissionModule) => boolean
  isRoleHigherOrEqual: (requiredRole: UserRole) => boolean
  permissions: ReturnType<typeof getRolePermissions>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [tenant, setTenant] = useState<AuthTenant | null>(null)
  const [token, setToken] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [permissionsVersion, setPermissionsVersion] = useState(0)

  // Load auth state and permissions on mount
  useEffect(() => {
    const init = async () => {
      const savedUser = getUser()
      const savedTenant = getTenant()
      const savedToken = getToken()

      if (savedUser) setUser(savedUser)
      if (savedTenant) setTenant(savedTenant)
      if (savedToken) setToken(savedToken)

      await loadPermissionsFromAPI()
      setPermissionsVersion(v => v + 1)
      setIsLoading(false)
    }
    init()
  }, [])

  const login = useCallback(async (email: string, password: string) => {
    try {
      console.log('[AUTH] Attempting login for:', email)
      const result = await loginWithBackend(email, password)
      console.log('[AUTH] Login success:', result.user.email)
      setToken(result.token)

      const newUser: AuthUser = {
        id: result.user.id,
        tenantId: result.user.tenantId,
        email: result.user.email,
        name: result.user.name,
        role: result.user.role || "agent",
      }
      authSetUser(newUser)
      setUser(newUser)

      if (result.tenant) {
        authSelectTenant(result.tenant)
        setTenant(result.tenant)
      }

      return { success: true, user: newUser, tenants: result.tenant ? [result.tenant] : [] }
    } catch (error) {
      console.error('[AUTH] Login failed:', error)
      const message = error instanceof Error ? error.message : "Login failed"
      return { success: false, error: message }
    }
  }, [])

  const logout = useCallback(() => {
    authLogout()
    setUser(null)
    setTenant(null)
    setToken(null)
  }, [])

  const selectTenantCb = useCallback((selectedTenant: AuthTenant) => {
    authSelectTenant(selectedTenant)
    setTenant(selectedTenant)
    if (user) {
      const updated = { ...user, tenantId: selectedTenant.id }
      authSetUser(updated)
      setUser(updated)
    }
  }, [user])

  const updateTenant = useCallback((updatedTenant: AuthTenant) => {
    setTenant(updatedTenant)
    if (typeof window !== "undefined") {
      localStorage.setItem("crm_auth_tenant", JSON.stringify(updatedTenant))
    }
  }, [])

  // Permission helpers bound to current user's role
  const userRole = user?.role || "viewer"

  const checkPermission = useCallback((module: PermissionModule, action: PermissionAction) => {
    return hasPermission(userRole, module, action)
  }, [userRole, permissionsVersion])

  const checkCanView = useCallback((module: PermissionModule) => {
    return canView(userRole, module)
  }, [userRole, permissionsVersion])

  const checkCanCreate = useCallback((module: PermissionModule) => {
    return canCreate(userRole, module)
  }, [userRole, permissionsVersion])

  const checkCanEdit = useCallback((module: PermissionModule) => {
    return canEdit(userRole, module)
  }, [userRole, permissionsVersion])

  const checkCanDelete = useCallback((module: PermissionModule) => {
    return canDelete(userRole, module)
  }, [userRole, permissionsVersion])

  const checkRoleHigherOrEqual = useCallback((requiredRole: UserRole) => {
    return isRoleHigherOrEqual(userRole, requiredRole)
  }, [userRole])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const permissions = getRolePermissions(userRole)

  const value: AuthContextType = {
    user,
    tenant,
    token,
    isLoading,
    isAuthenticated: user !== null && tenant !== null,
    login,
    logout,
    selectTenant: selectTenantCb,
    updateTenant,
    hasPermission: checkPermission,
    canView: checkCanView,
    canCreate: checkCanCreate,
    canEdit: checkCanEdit,
    canDelete: checkCanDelete,
    isRoleHigherOrEqual: checkRoleHigherOrEqual,
    permissions,
  }

  return (
    <AuthContext.Provider value={value}>
      <PermissionsProvider token={token} tenantId={tenant?.id ?? null}>
        {children}
      </PermissionsProvider>
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider")
  }
  return context
}

// Hook for checking specific permissions
export function usePermission(module: PermissionModule, action: PermissionAction) {
  const { hasPermission } = useAuth()
  return hasPermission(module, action)
}

// Hook for getting all permissions for a module
export function useModulePermissions(module: PermissionModule) {
  const { canView, canCreate, canEdit, canDelete, hasPermission } = useAuth()

  return {
    canView: canView(module),
    canCreate: canCreate(module),
    canEdit: canEdit(module),
    canDelete: canDelete(module),
    canAssign: hasPermission(module, "assign"),
    canSend: hasPermission(module, "send"),
    canExport: hasPermission(module, "export"),
  }
}
